import { useLocaleInfo } from "@/hooks/useLocaleInfo";

type HotelGalleryIndicatorsProps = {
  totalImages: number;
  currentIndex: number;
};

const DOT_SIZE_ACTIVE = 6;
const DOT_SIZE_INACTIVE = 4;
// All dots occupy the active slot size so the container never changes width.
const DOT_SLOT = DOT_SIZE_ACTIVE;
const GAP = 4;
const VISIBLE_COUNT = 7;
const CENTER_OFFSET = Math.floor(VISIBLE_COUNT / 2);

export const HotelGalleryIndicators = ({
  totalImages,
  currentIndex,
}: HotelGalleryIndicatorsProps) => {
  const { dir } = useLocaleInfo();

  if (totalImages <= 1) return null;

  const visibleCount = Math.min(totalImages, VISIBLE_COUNT);
  const windowWidth = visibleCount * DOT_SLOT + (visibleCount - 1) * GAP;

  const windowShiftIndex = Math.max(
    CENTER_OFFSET,
    Math.min(currentIndex, totalImages - 1 - CENTER_OFFSET),
  );
  const pan =
    totalImages > VISIBLE_COUNT
      ? (windowShiftIndex - CENTER_OFFSET) * (DOT_SLOT + GAP)
      : 0;
  // In RTL the dot strip flows right→left, so we pan in the positive direction
  const translateX = dir === "rtl" ? pan : -pan;

  return (
    <div className="absolute start-1/2 bottom-3 z-10 -translate-x-1/2 overflow-hidden rounded-full bg-gray-darken-500-d px-1 py-[2px] rtl:translate-x-1/2">
      <div
        className="flex items-center gap-1 transition-transform duration-300 ease-out"
        style={{
          width: `${windowWidth}px`,
          transform: `translateX(${translateX}px)`,
        }}
      >
        {Array.from({ length: totalImages }, (_, idx) => {
          const isActive = idx === currentIndex;
          const size = isActive ? DOT_SIZE_ACTIVE : DOT_SIZE_INACTIVE;
          return (
            <div
              key={idx}
              className={`shrink-0 rounded-full transition-all duration-300 ${isActive ? "bg-white" : "bg-white/40"}`}
              style={{ width: size, height: size }}
            />
          );
        })}
      </div>
    </div>
  );
};
